import { defineComponent as _defineComponent } from 'vue'
import BCXMessengerChatMessage from '@/components/messenger/Chat/BCXMessengerChatMessage.vue';
import { Chat, ChatMessage, TEMPORARY_CHAT_USER_ID } from '@/models/Messenger';
import { User } from '@/models/User';
import useMessengerState from '@/state/messenger/messengerState';
import scrollIntoView from '@/utils/scrollIntoView';
import {
  computed, nextTick, onMounted, PropType, Ref, ref, toRefs, watch, watchEffect
} from 'vue';
import { createVuexHelpers } from 'vue2-helpers';
import {
  templateRef, useDebounce, useDocumentVisibility, useElementVisibility, useEventListener, useIntersectionObserver
} from '@vueuse/core';
import onSizeChange from '@/mixins/onSizeChange';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXMessengerChatMessagesPaged',
  props: {
  chat: {
    type: Object as PropType<Chat>,
    default: () => null,
  },
},
  setup(__props) {

const props = __props;



const {
  isInputFocussed, isExpanded, isInputMaximized, editMessageId,
  groupHasMore, isLoadingOlderMessages, loadOlderGroupMessages,
  groupWindowChatId, markGroupSeen, perGroupMeta
} = useMessengerState();
const { chat } = toRefs(props);
const { useGetters } = createVuexHelpers();
const { user } = useGetters(['user']) as { user: Ref<User> };

const isExpandedAfterAnimation = useDebounce(isExpanded, 500);
const messages = templateRef('messages') as Ref<HTMLElement>;
const sentinel = templateRef('sentinel') as Ref<HTMLElement | null>;

// Prepended history renders without BCXLazy so the restored scroll
// position does not drift. Mutated pre-patch, read by the same render.
const eagerIds = ref(new Set<string>());

const editedMessageElm = computed(() => (editMessageId.value && messages.value
  ? messages.value.querySelector(`#msg-${editMessageId.value}`) : null));

const scrollToBottom = () => {
  if (editedMessageElm.value) {
    scrollIntoView(editedMessageElm.value as HTMLElement, messages.value);
  } else if (messages.value) {
    const { scrollHeight, offsetHeight } = messages.value;
    messages.value.scrollTo(0, scrollHeight - offsetHeight);
  }
};

const isAtBottom = () => {
  const el = messages.value;
  if (!el) return true;
  return el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
};

// Updated on every (also programmatic) scroll; read before re-renders.
const wasAtBottom = ref(true);
useEventListener(messages, 'scroll', () => {
  wasAtBottom.value = isAtBottom();
}, { passive: true });

const shouldStickToBottom = () => wasAtBottom.value || !!editedMessageElm.value;

// Re-pin while pinned: lazy bodies expand after the initial scroll.
const onMessageShown = () => {
  if (shouldStickToBottom()) scrollToBottom();
};

// Load older messages ~2 chat heights before reaching the top. vueuse
// recreates the observer per re-render; its initial callback re-fires
// while the sentinel is still inside the margin, continuing the load.
useIntersectionObserver(sentinel, (entries) => {
  if (entries[entries.length - 1]?.isIntersecting) loadOlderGroupMessages();
}, { root: messages, rootMargin: '800px 0px 0px 0px' });

// Mark read only while the newest message is really in view.
const anchor = templateRef('anchor') as Ref<HTMLElement | null>;
const isAnchorVisible = useElementVisibility(anchor);
const documentVisibility = useDocumentVisibility();
const isSeen = () => isAnchorVisible.value && documentVisibility.value === 'visible'
  && groupWindowChatId.value === chat.value?.id;
const hasUnread = computed(() => (perGroupMeta.value[chat.value?.id]?.unreadCount ?? 0) > 0
  || !!chat.value?.messages?.some((m) => !m.read));
watchEffect(() => {
  if (isSeen() && hasUnread.value && chat.value?.messages?.length) {
    markGroupSeen(chat.value.id, isSeen);
  }
});

const firstId = (list?: ChatMessage[]) => list?.[0]?.messageId;
const lastId = (list?: ChatMessage[]) => list?.[list.length - 1]?.messageId;

// Registered in setup so it runs before the render watcher: scroll metrics
// are captured pre-patch and eagerIds reaches the triggering render.
watch(() => chat.value?.messages, (newMessages, oldMessages) => {
  const el = messages.value;
  const oldFirst = firstId(oldMessages);
  const oldLast = lastId(oldMessages);
  if (firstId(newMessages) === oldFirst && lastId(newMessages) === oldLast) return;

  const oldFirstIndex = oldFirst ? (newMessages?.findIndex((m) => m.messageId === oldFirst) ?? -1) : -1;
  const newIds = new Set(newMessages?.map((m) => m.messageId));
  const isOldContained = !!oldMessages?.every((m) => newIds.has(m.messageId));

  // Prepend (possibly coalesced with an append): old window contained in new.
  if (oldFirstIndex > 0 && isOldContained && el) {
    newMessages?.slice(0, oldFirstIndex).forEach((m) => eagerIds.value.add(m.messageId));
    const { scrollTop, scrollHeight } = el;
    nextTick(() => {
      el.scrollTop = scrollTop + (el.scrollHeight - scrollHeight);
    });
    return;
  }

  // Append: pin only when at bottom or when the tail contains an own message.
  if (oldFirstIndex === 0) {
    const oldIds = new Set(oldMessages?.map((m) => m.messageId));
    const appended = newMessages?.filter((m) => !oldIds.has(m.messageId)) ?? [];
    // Appended messages are at the viewport anyway; eager avoids a lazy flicker.
    appended.forEach((m) => eagerIds.value.add(m.messageId));
    const hasOwnMessage = appended.some((m) => m.creator === user.value?.userId || m.creator === TEMPORARY_CHAT_USER_ID);
    if (!shouldStickToBottom() && !hasOwnMessage) return;
    nextTick(() => scrollToBottom());
    return;
  }

  // Initial load or window reset.
  eagerIds.value = new Set();
  nextTick(() => scrollToBottom());
});

onMounted(() => {
  watch([isExpandedAfterAnimation, isInputFocussed, isInputMaximized], ([curExpanded, isFocussed, isMaximized],
    [prevExpanded, prevIsFocussed, prevIsMaximized]) => {
    if ((curExpanded !== prevExpanded
      || isFocussed !== prevIsFocussed
      || isMaximized !== prevIsMaximized) && shouldStickToBottom()) {
      scrollToBottom();
    }
  }, {
    immediate: true,
  });
  scrollToBottom();
});

onSizeChange(messages, ({ height, oldHeight }) => {
  if (height < oldHeight && shouldStickToBottom()) {
    scrollToBottom();
  }
});

const sortedMessages = computed(() => [...chat.value.messages].sort((msgA, msgB) => msgA.created.localeCompare(msgB.created)));

return { __sfc: true,props, isInputFocussed, isExpanded, isInputMaximized, editMessageId, groupHasMore, isLoadingOlderMessages, loadOlderGroupMessages, groupWindowChatId, markGroupSeen, perGroupMeta, chat, useGetters, user, isExpandedAfterAnimation, messages, sentinel, eagerIds, editedMessageElm, scrollToBottom, isAtBottom, wasAtBottom, shouldStickToBottom, onMessageShown, anchor, isAnchorVisible, documentVisibility, isSeen, hasUnread, firstId, lastId, sortedMessages, BCXMessengerChatMessage }
}

})